import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BackHandler, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { useSocket } from '../hooks/useSocket';

interface CallScreenParams {
  callId: string;
  isVideo: boolean;
}

type CallScreenRouteProp = RouteProp<{ params: CallScreenParams }, 'params'>;

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// ---- 通话控制栏 ----
function CallControls({
  isVideo,
  isMuted,
  isSpeakerEnabled,
  isCameraOff,
  onToggleMute,
  onToggleSpeaker,
  onToggleCamera,
  onEndCall,
}: {
  isVideo: boolean;
  isMuted: boolean;
  isSpeakerEnabled: boolean;
  isCameraOff: boolean;
  onToggleMute: () => void;
  onToggleSpeaker: () => void;
  onToggleCamera: () => void;
  onEndCall: () => void;
}) {
  return (
    <View style={styles.controls}>
      <TouchableOpacity
        style={[styles.controlButton, isMuted && styles.controlButtonActive]}
        onPress={onToggleMute}
      >
        <Text style={styles.controlIcon}>{isMuted ? '🔇' : '🎤'}</Text>
      </TouchableOpacity>

      {isVideo && (
        <TouchableOpacity
          style={[styles.controlButton, isCameraOff && styles.controlButtonActive]}
          onPress={onToggleCamera}
        >
          <Text style={styles.controlIcon}>{isCameraOff ? '📷' : '📹'}</Text>
        </TouchableOpacity>
      )}

      <TouchableOpacity
        style={[styles.controlButton, isSpeakerEnabled && styles.controlButtonActive]}
        onPress={onToggleSpeaker}
      >
        <Text style={styles.controlIcon}>{isSpeakerEnabled ? '🔊' : '🔈'}</Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.endButton} onPress={onEndCall}>
        <Text style={styles.endIcon}>📞</Text>
      </TouchableOpacity>
    </View>
  );
}

// ---- 语音通话视图 ----
function VoiceCallView({
  isConnected,
  connectionState,
}: {
  isConnected: boolean;
  connectionState: string;
}) {
  return (
    <View style={styles.voiceContainer}>
      <Text style={styles.voiceIcon}>📞</Text>
      {!isConnected && <Text style={styles.connectingText}>连接中...</Text>}
      {connectionState === 'connecting' && <Text style={styles.statusText}>正在建立连接...</Text>}
      {connectionState === 'failed' && <Text style={styles.errorText}>连接失败</Text>}
    </View>
  );
}

// ---- 视频通话视图 ----
function VideoCallView({
  isConnected,
  isCameraOff,
  localStream,
  remoteStream,
  onSwitchCamera,
}: {
  isConnected: boolean;
  isCameraOff: boolean;
  localStream: any;
  remoteStream: any;
  onSwitchCamera: () => void;
}) {
  return (
    <>
      {/* 远端视频 */}
      <View style={styles.remoteVideo}>
        {remoteStream ? (
          <Text style={styles.videoPlaceholder}>对方视频画面</Text>
        ) : (
          <Text style={styles.videoPlaceholder}>{isConnected ? '对方画面' : '连接中...'}</Text>
        )}
      </View>

      {/* 本地视频 */}
      <View style={styles.localVideo}>
        {isCameraOff ? (
          <Text style={styles.cameraOffText}>📷</Text>
        ) : localStream ? (
          <View style={styles.localVideoContainer}>
            <Text style={styles.videoPlaceholderSmall}>本地画面</Text>
          </View>
        ) : (
          <TouchableOpacity style={styles.switchCameraButton} onPress={onSwitchCamera}>
            <Text style={styles.switchCameraText}>🔄</Text>
          </TouchableOpacity>
        )}
      </View>
    </>
  );
}

// ---- 主屏幕 ----
export default function CallScreen() {
  const route = useRoute<CallScreenRouteProp>();
  const navigation = useNavigation();
  const { callId, isVideo } = route.params;
  const { emit } = useSocket();

  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('new');
  const [localStream, setLocalStream] = useState<any>(null);
  const [remoteStream, setRemoteStream] = useState<any>(null);

  const peerConnectionRef = useRef<any>(null);
  const remoteStreamRef = useRef<any>(null);

  const cleanup = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((track: any) => track.stop());
      remoteStreamRef.current = null;
    }
  }, []);

  // 通话计时器
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    // 模拟连接（实际项目中由 WebRTC 管理）
    const connectTimer = setTimeout(() => setIsConnected(true), 2000);

    return () => {
      clearInterval(timer);
      clearTimeout(connectTimer);
      cleanup();
    };
  }, [cleanup]);

  // 禁止返回键
  useEffect(() => {
    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => backHandler.remove();
  }, []);

  const handleEndCall = useCallback(() => {
    emit('call:end', { callId });
    cleanup();
    navigation.goBack();
  }, [callId, emit, cleanup, navigation]);

  const toggleMute = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
      }
    }
    setIsMuted((prev) => !prev);
  }, [localStream]);

  const toggleSpeaker = useCallback(() => {
    setIsSpeakerEnabled((prev) => !prev);
  }, []);

  const toggleCamera = useCallback(() => {
    if (localStream && isVideo) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
      }
    }
    setIsCameraOff((prev) => !prev);
  }, [localStream, isVideo]);

  const switchCamera = useCallback(() => {
    if (localStream && isVideo) {
      localStream.getVideoTracks().forEach((track: any) => track._switchCamera());
    }
  }, [localStream, isVideo]);

  // 监听连接状态变化
  useEffect(() => {
    if (!peerConnectionRef.current) return;
    const pc = peerConnectionRef.current;

    const handleConnectionStateChange = () => {
      setConnectionState(pc.connectionState);
      if (pc.connectionState === 'connected') {
        setIsConnected(true);
      } else if (pc.connectionState === 'failed') {
        setIsConnected(false);
      }
    };

    pc.addEventListener('connectionstatechange', handleConnectionStateChange);
    return () => {
      pc.removeEventListener('connectionstatechange', handleConnectionStateChange);
    };
  }, [connectionState]);

  return (
    <View style={styles.container}>
      {isVideo ? (
        <VideoCallView
          isConnected={isConnected}
          isCameraOff={isCameraOff}
          localStream={localStream}
          remoteStream={remoteStream}
          onSwitchCamera={switchCamera}
        />
      ) : (
        <VoiceCallView isConnected={isConnected} connectionState={connectionState} />
      )}

      {/* 通话时长 */}
      <View style={styles.durationBar}>
        <Text style={styles.duration}>{formatDuration(callDuration)}</Text>
      </View>

      {/* 控制栏 */}
      <CallControls
        isVideo={isVideo}
        isMuted={isMuted}
        isSpeakerEnabled={isSpeakerEnabled}
        isCameraOff={isCameraOff}
        onToggleMute={toggleMute}
        onToggleSpeaker={toggleSpeaker}
        onToggleCamera={toggleCamera}
        onEndCall={handleEndCall}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  cameraOffText: {
    fontSize: 40,
  },
  connectingText: {
    color: '#999',
    fontSize: 16,
    marginTop: 20,
  },
  container: {
    backgroundColor: '#000',
    flex: 1,
  },
  controlButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 30,
    height: 60,
    justifyContent: 'center',
    width: 60,
  },
  controlButtonActive: {
    backgroundColor: 'rgba(201, 162, 39, 0.6)',
  },
  controlIcon: {
    fontSize: 24,
  },
  controls: {
    bottom: 40,
    flexDirection: 'row',
    justifyContent: 'space-around',
    left: 0,
    paddingHorizontal: 20,
    position: 'absolute',
    right: 0,
  },
  duration: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  durationBar: {
    alignItems: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 60,
  },
  endButton: {
    alignItems: 'center',
    backgroundColor: '#ff4d4f',
    borderRadius: 35,
    height: 70,
    justifyContent: 'center',
    width: 70,
  },
  endIcon: {
    fontSize: 28,
  },
  errorText: {
    color: '#ff4d4f',
    fontSize: 14,
    marginTop: 10,
  },
  localVideo: {
    alignItems: 'center',
    backgroundColor: '#333',
    borderRadius: 8,
    bottom: 200,
    height: 160,
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'absolute',
    right: 16,
    width: 120,
  },
  localVideoContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  remoteVideo: {
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    flex: 1,
    justifyContent: 'center',
  },
  statusText: {
    color: '#999',
    fontSize: 14,
    marginTop: 10,
  },
  switchCameraButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 15,
    bottom: 8,
    height: 30,
    justifyContent: 'center',
    position: 'absolute',
    right: 8,
    width: 30,
  },
  switchCameraText: {
    fontSize: 16,
  },
  videoPlaceholder: {
    color: '#999',
    fontSize: 14,
  },
  videoPlaceholderSmall: {
    color: '#999',
    fontSize: 12,
  },
  voiceContainer: {
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    flex: 1,
    justifyContent: 'center',
  },
  voiceIcon: {
    fontSize: 80,
  },
});
